import { useState } from 'react'
import type { FormEvent } from 'react'
import { Loader2 } from 'lucide-react'
import {
  AgentQuestionnaireStatusEnum,
  AgentQuestionTypeEnum,
  type AgentQuestionnaire,
} from '@/gen/cortado/v1/agent_pb'
import type { QuestionnaireAnswerInput } from '@/contexts/ai-assistant-context'
import { Badge } from '../ui/badge'
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../ui/card'
import {
  Questionnaire,
  QuestionnaireActions,
  QuestionnaireChoice,
  QuestionnaireChoiceInput,
  QuestionnaireChoiceLabel,
  QuestionnaireChoiceShortcut,
  QuestionnaireChoices,
  QuestionnaireDescription,
  QuestionnaireError,
  QuestionnaireInput,
  QuestionnaireItem,
  QuestionnaireNext,
  QuestionnairePrevious,
  QuestionnaireProgress,
  QuestionnaireSkip,
  QuestionnaireSubmit,
  QuestionnaireTitle,
} from '../ui/questionnaire'

export function AgentQuestionnaireCard({
  questionnaire,
  onSubmit,
}: {
  questionnaire: AgentQuestionnaire
  onSubmit: (questionnaireId: string, answers: QuestionnaireAnswerInput[]) => Promise<void>
}) {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const answersByQuestion = new Map(questionnaire.answers.map((answer) => [answer.questionId, answer.values]))
  const isProcessing = questionnaire.status === AgentQuestionnaireStatusEnum.AGENT_QUESTIONNAIRE_STATUS_PROCESSING

  if (questionnaire.status === AgentQuestionnaireStatusEnum.AGENT_QUESTIONNAIRE_STATUS_ANSWERED || isProcessing) {
    return (
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between gap-3">
            <CardTitle>{questionnaire.title}</CardTitle>
            <Badge variant="secondary">
              {isProcessing && <Loader2 data-icon="inline-start" className="animate-spin" />}
              {isProcessing ? 'Submitting' : 'Answered'}
            </Badge>
          </div>
          {questionnaire.description && <CardDescription>{questionnaire.description}</CardDescription>}
        </CardHeader>
        <CardContent>
          <dl className="flex flex-col gap-3 text-sm">
            {questionnaire.questions.map((question) => {
              const value = answersByQuestion.get(question.id)?.[0]
              if (!value) return null
              const displayValue = question.choices.find((choice) => choice.value === value)?.label ?? value
              return (
                <div key={question.id} className="flex flex-col gap-1">
                  <dt className="text-muted-foreground">{question.prompt}</dt>
                  <dd>{displayValue}</dd>
                </div>
              )
            })}
          </dl>
        </CardContent>
      </Card>
    )
  }

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (isSubmitting) return
    const formData = new FormData(event.currentTarget)
    const answers = questionnaire.questions.flatMap<QuestionnaireAnswerInput>((question) => {
      const values = question.type === AgentQuestionTypeEnum.AGENT_QUESTION_TYPE_SINGLE_CHOICE
        ? formData.getAll(question.id).map(String).filter(Boolean)
        : [String(formData.get(question.id) ?? '').trim()].filter(Boolean)
      return values.length > 0 ? [{ questionId: question.id, values }] : []
    })

    setIsSubmitting(true)
    try {
      await onSubmit(questionnaire.id, answers)
    } finally {
      setIsSubmitting(false)
    }
  }

  const items = questionnaire.questions.map((question) => ({
    name: question.id,
    required: question.required,
    disabled: isSubmitting,
    choices: question.type === AgentQuestionTypeEnum.AGENT_QUESTION_TYPE_SINGLE_CHOICE
      ? question.choices.map((choice) => ({ value: choice.value }))
      : undefined,
  }))

  return (
    <Questionnaire items={items} shortcuts="letters" onSubmit={(event) => void handleSubmit(event)}>
      <Card>
        <CardHeader>
          <CardTitle>{questionnaire.title}</CardTitle>
          {questionnaire.description && <CardDescription>{questionnaire.description}</CardDescription>}
          <QuestionnaireProgress />
        </CardHeader>
        <CardContent>
          {questionnaire.questions.map((question) => (
            <QuestionnaireItem
              key={question.id}
              name={question.id}
              required={question.required}
              disabled={isSubmitting}
            >
              <QuestionnaireTitle>{question.prompt}</QuestionnaireTitle>
              {question.description && (
                <QuestionnaireDescription>{question.description}</QuestionnaireDescription>
              )}
              {question.type === AgentQuestionTypeEnum.AGENT_QUESTION_TYPE_SINGLE_CHOICE ? (
                <QuestionnaireChoices>
                  {question.choices.map((choice) => (
                    <QuestionnaireChoice
                      key={choice.value}
                      value={choice.value}
                      defaultChecked={answersByQuestion.get(question.id)?.includes(choice.value)}
                    >
                      <QuestionnaireChoiceInput />
                      <QuestionnaireChoiceLabel>
                        <span>{choice.label}</span>
                        {choice.description && (
                          <span className="block text-xs text-muted-foreground">{choice.description}</span>
                        )}
                      </QuestionnaireChoiceLabel>
                      <QuestionnaireChoiceShortcut />
                    </QuestionnaireChoice>
                  ))}
                </QuestionnaireChoices>
              ) : (
                <QuestionnaireInput
                  aria-label={question.prompt}
                  defaultValue={answersByQuestion.get(question.id)?.[0]}
                />
              )}
              <QuestionnaireError>Answer this question to continue.</QuestionnaireError>
            </QuestionnaireItem>
          ))}
        </CardContent>
        <CardFooter>
          <QuestionnaireActions className="w-full">
            <QuestionnairePrevious disabled={isSubmitting} />
            <div className="ml-auto flex items-center gap-2">
              <QuestionnaireSkip disabled={isSubmitting} />
              <QuestionnaireNext disabled={isSubmitting} />
              <QuestionnaireSubmit disabled={isSubmitting}>
                {isSubmitting && <Loader2 data-icon="inline-start" className="animate-spin" />}
                {isSubmitting ? 'Submitting…' : 'Submit answers'}
              </QuestionnaireSubmit>
            </div>
          </QuestionnaireActions>
        </CardFooter>
      </Card>
    </Questionnaire>
  )
}
